import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/casestudy.css';
import '../styles/portfolio.css';
import projectsData from '../projects/projectsData';
import NavBar from '../NavBar';
import { ProjectPreview } from '../components/ProjectPreviews';

const LABELS = {
  en: {
    role: 'Role', timeline: 'Timeline', teamSize: 'Team Size',
    background: 'Background', problem: 'The Problem', goals: 'Project Goals',
    research: 'Research & Discovery', userPersona: 'User Persona',
    empathyMap: 'Empathy Map', userInterviews: 'User Interview Findings',
    secondaryResearch: 'Secondary Research', competitive: 'Competitive Analysis',
    solution: 'The Solution', userFlow: 'User Flow', userJourney: 'User Journey',
    infoArch: 'Information Architecture', systemDiagram: 'System Diagram',
    wireframes: 'Wireframes & Prototypes', styleGuide: 'Style Guide',
    hifiWireframes: 'High-Fidelity Wireframes', prototype: 'Interactive Prototype',
    usabilityTesting: 'Usability Testing', userFeedback: 'User Feedback',
    conclusion: 'Conclusion', keyLearnings: 'Key Learnings',
    inProgress: 'In Progress', csSoon: 'Case study coming soon.',
    csSoonDesc: "I'm still writing this one up — full process, research, and design breakdowns coming soon.",
    website: 'Website', download: 'Download', github: 'GitHub', liveDemo: 'Live Demo',
  },
  ar: {
    role: 'الدور', timeline: 'الجدول الزمني', teamSize: 'حجم الفريق',
    background: 'خلفية', problem: 'المشكلة', goals: 'أهداف المشروع',
    research: 'البحث والاستكشاف', userPersona: 'شخصية المستخدم',
    empathyMap: 'خريطة التعاطف', userInterviews: 'نتائج المقابلات',
    secondaryResearch: 'البحث الثانوي', competitive: 'التحليل التنافسي',
    solution: 'الحل', userFlow: 'تدفق المستخدم', userJourney: 'رحلة المستخدم',
    infoArch: 'هندسة المعلومات', systemDiagram: 'مخطط النظام',
    wireframes: 'النماذج الأولية', styleGuide: 'دليل الأسلوب',
    hifiWireframes: 'نماذج عالية الدقة', prototype: 'نموذج تفاعلي',
    usabilityTesting: 'اختبار قابلية الاستخدام', userFeedback: 'ملاحظات المستخدمين',
    conclusion: 'الخلاصة', keyLearnings: 'الدروس المستفادة',
    inProgress: 'قيد الإنجاز', csSoon: 'دراسة الحالة قريباً.',
    csSoonDesc: 'ما زلت أكتب هذه الدراسة — التفاصيل الكاملة قريباً.',
    website: 'الموقع', download: 'تحميل', github: 'GitHub', liveDemo: 'تجربة مباشرة',
  },
};

const CaseStudy = () => {
  const { id } = useParams();
  const project = projectsData[id];

  const [dark, setDark] = useState(() => localStorage.getItem('pf-dark') === 'true');
  const [lang, setLang] = useState('en');

  const L = LABELS[lang];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.setAttribute('dir', 'ltr');
    document.documentElement.setAttribute('lang', 'en');
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('pf-dark', dark);
  }, [dark]);

  if (!project) {
    return <div>Project not found</div>;
  }

  const hasLinks = project.website || project.github || project.appDownload || project.demo;

  return (
    <div className="case-study-container">
      <NavBar
        dark={dark}
        onDarkChange={setDark}
        lang={lang}
        onLangChange={setLang}
      />

      {/* Hero Section */}
      <div className="case-study-hero">
        <div className="hero-content">
          <h1 className="project-title">{project.title}</h1>
          <p className="project-description">{project.description}</p>

          {/* Action Buttons */}
          {hasLinks && (
            <div className="cs-project-links">
              {project.website && (
                <a href={project.website} target="_blank" rel="noopener noreferrer" className="cs-link-btn">
                  {L.website} <span>↗</span>
                </a>
              )}
              {project.appDownload && (
                <a href={project.appDownload} target="_blank" rel="noopener noreferrer" className="cs-link-btn">
                  {L.download} <span>↗</span>
                </a>
              )}
              {project.github && (
                <a href={project.github} target="_blank" rel="noopener noreferrer" className="cs-link-btn cs-link-btn--ghost">
                  {L.github} <span>↗</span>
                </a>
              )}
              {project.demo && !project.website && (
                <a href={project.demo} target="_blank" rel="noopener noreferrer" className="cs-link-btn">
                  {L.liveDemo} <span>↗</span>
                </a>
              )}
            </div>
          )}
        </div>
        <div className="hero-image">
          {(() => {
            const numId = typeof id === 'string' && !isNaN(id) ? Number(id) : id;
            const hasFeaturedPreview = id === 'malabas' || id === 'silsila' || numId === 4 || numId === 2;
            if (hasFeaturedPreview) {
              return <ProjectPreview slug={id} id={numId} color={project.color} />;
            }
            return project.image
              ? <img src={project.image} alt={project.title} />
              : <ProjectPreview slug={id} id={numId} color={project.color} />;
          })()}
        </div>
      </div>

      {/* Project Details */}
      <div className="project-details">
        <div className="detail-box">
          <h3>{L.role}</h3>
          <p>{project.role}</p>
        </div>
        <div className="detail-box">
          <h3>{L.timeline}</h3>
          <p>{project.timeline || '3 months'}</p>
        </div>
        <div className="detail-box">
          <h3>{L.teamSize}</h3>
          <p>{project.teamSize || '1 person'}</p>
        </div>
      </div>

      {/* Main Content */}
      {project.comingSoon ? (
        <div className="cs-coming-soon-block">
          <div className="cs-coming-soon-badge">{L.inProgress}</div>
          <h2>{L.csSoon}</h2>
          <p>{L.csSoonDesc}</p>
        </div>
      ) : (
        <div className="case-study-content">
          {/* Background */}
          <section className="content-section">
            <h2 className="section-title">{L.background}</h2>
            <p className="section-text">{project.background}</p>
          </section>

          {/* Problem */}
          <section className="content-section problem-section">
            <h2 className="section-title">{L.problem}</h2>
            <p className="section-text">{project.problem}</p>
            <div className="problem-details">
              {project.userProblems && project.userProblems.map((item, i) => (
                <div key={i} className="problem-item">{item.text}</div>
              ))}
            </div>
          </section>

          {/* Goals */}
          <section className="content-section">
            <h2 className="section-title">{L.goals}</h2>
            <div className="goals-container">
              {project.goals && project.goals.map((item, i) => (
                <div key={i} className="goal-item">{item.text}</div>
              ))}
            </div>
          </section>

          {/* Research */}
          <section className="content-section">
            <h2 className="section-title">{L.research}</h2>

            {project.userpersonapic && (
              <div className="research-visual">
                <h3 className="subsection-title">{L.userPersona}</h3>
                <img src={project.userpersonapic} alt={L.userPersona} className="research-image" />
              </div>
            )}
            {project.empathymap && (
              <div className="research-visual">
                <h3 className="subsection-title">{L.empathyMap}</h3>
                <img src={project.empathymap} alt={L.empathyMap} className="research-image" />
              </div>
            )}
            <div className="research-findings">
              <h3 className="subsection-title">{L.userInterviews}</h3>
              <div className="findings-grid">
                {project.userInterviews && project.userInterviews.map((item, i) => (
                  <div key={i} className="finding-item">{item.text}</div>
                ))}
              </div>
            </div>
            <div className="research-findings">
              <h3 className="subsection-title">{L.secondaryResearch}</h3>
              <div className="findings-grid">
                {project.research && project.research.map((item, i) => (
                  <div key={i} className="finding-item">{item.text}</div>
                ))}
              </div>
            </div>
            <div className="research-findings">
              <h3 className="subsection-title">{L.competitive}</h3>
              <p>{project.competitiveResearch}</p>
              <div className="competitors-grid">
                {project.competition && project.competition.map((item, i) => (
                  <div key={i} className="competitor-item">{item.text}</div>
                ))}
              </div>
            </div>
          </section>

          {/* Solution */}
          <section className="content-section solution-section">
            <h2 className="section-title">{L.solution}</h2>
            <p className="section-text">{project.solution}</p>
            <div className="solutions-grid">
              {project.ourSolutions && project.ourSolutions.map((item, i) => (
                <div key={i} className="solution-item">{item.text}</div>
              ))}
            </div>
          </section>

          {/* Design Process */}
          <section className="content-section">
            {project.designprocess && (
              <div className="design-visual">
                <h2 className="subsection-title">Design Process</h2>
                <img src={project.designprocess} alt="Design Process" className="design-image" />
              </div>
            )}
            {project.userflow && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.userFlow}</h3>
                <img src={project.userflow} alt={L.userFlow} className="design-image" />
              </div>
            )}
            {project.journeymap && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.userJourney}</h3>
                <img src={project.journeymap} alt={L.userJourney} className="design-image" />
              </div>
            )}
            {(project.infoarch && project.infoarch.length > 0) && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.infoArch}</h3>
                <div className="architecture-images">
                  {project.infoarch.map((arch, i) => (
                    <img key={i} src={arch} alt={`${L.infoArch} ${i + 1}`} className="design-image" />
                  ))}
                </div>
              </div>
            )}
            {project.umldiagram && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.systemDiagram}</h3>
                <img src={project.umldiagram} alt={L.systemDiagram} className="design-image" />
              </div>
            )}
          </section>

          {/* Wireframes */}
          <section className="content-section">
            <h2 className="section-title">{L.wireframes}</h2>
            <div className="wireframes-container">
              <div className="wireframes-grid">
                {project.lofiPic && (Array.isArray(project.lofiPic) ? project.lofiPic : [project.lofiPic]).map((pic, i) => (
                  <img key={i} src={pic} alt={`Lo-fi Wireframe ${i + 1}`} className="wireframe-image" />
                ))}
              </div>
            </div>
            {project.styleguideline && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.styleGuide}</h3>
                <img src={project.styleguideline} alt={L.styleGuide} className="design-image" />
              </div>
            )}
            {project.hifiWireframes && (
              <div className="design-visual">
                <h3 className="subsection-title">{L.hifiWireframes}</h3>
                <img src={project.hifiWireframes} alt={L.hifiWireframes} className="design-image" />
              </div>
            )}
            {project.prototypeLink && (
              <div className="prototype-container">
                <h3 className="subsection-title">{L.prototype}</h3>
                <iframe className="prototype-frame" src={project.prototypeLink} allowFullScreen title={L.prototype}></iframe>
                {project.figma && id !== '0' && (
                  <a href={project.figma} className="figma-link" target="_blank" rel="noopener noreferrer">
                    View on Figma
                  </a>
                )}
              </div>
            )}
          </section>

          {/* Usability Testing */}
          <section className="content-section">
            <h2 className="section-title">{L.usabilityTesting}</h2>
            <p className="section-text">{project.usabilityTesting}</p>
            <div className="feedback-container">
              <h3 className="subsection-title">{L.userFeedback}</h3>
              <div className="feedback-grid">
                {project.feedback && project.feedback.map((item, i) => (
                  <div key={i} className="feedback-item">{item.text}</div>
                ))}
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="content-section conclusion-section">
            <h2 className="section-title">{L.conclusion}</h2>
            <p className="section-text">{project.conclusion}</p>
            {project.keyLearnings && (
              <div className="learnings-container">
                <h3 className="subsection-title">{L.keyLearnings}</h3>
                <div className="learnings-grid">
                  {project.keyLearnings.map((item, i) => (
                    <div key={i} className="learning-item">{item.text}</div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default CaseStudy;
